"use client";

import type { CSSProperties, ReactNode } from "react";
import {
	ArtistIcon,
	EmptyState,
	PlaylistPlaceholder,
} from "@/components/design-system/ArtistDesignSystem";
import type { LibraryPlaylist } from "./library-data";

export interface CollectionsGridViewProps {
	readonly playlists: readonly LibraryPlaylist[];
	readonly emptyTitle: string;
	readonly emptyMessage: string;
	readonly onPlaylistTap?: (playlist: LibraryPlaylist) => void;
}

const gridStyle: CSSProperties = {
	display: "grid",
	gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
	gap: 1,
	width: "100%",
};

const FAVORITES_GRADIENT =
	"linear-gradient(135deg, rgb(141 208 222 / 1), rgb(141 208 222 / 0.72))";

/**
 * CollectionsGridView — 3-column square grid of playlists. The default
 * favorites playlist renders a ★ cover on a soft accent gradient; other
 * playlists use their cover image or a deterministic placeholder.
 * Mirrors `CollectionsGridView` on iOS.
 */
export function CollectionsGridView({
	playlists,
	emptyTitle,
	emptyMessage,
	onPlaylistTap,
}: Readonly<CollectionsGridViewProps>) {
	if (playlists.length === 0) {
		return (
			<EmptyState
				className="library-empty"
				title={emptyTitle}
				message={emptyMessage}
				symbol={<ArtistIcon name="music" size={36} />}
			/>
		);
	}

	const ordered = orderPlaylists(playlists);

	return (
		<ul className="library-collections-grid" style={gridStyle}>
			{ordered.map((playlist) => (
				<li key={playlist.id} className="library-collections-grid__item">
					<PlaylistTile
						playlist={playlist}
						onTap={() => onPlaylistTap?.(playlist)}
					/>
				</li>
			))}
		</ul>
	);
}

function orderPlaylists(
	playlists: readonly LibraryPlaylist[],
): readonly LibraryPlaylist[] {
	const systemPlaylist = playlists.find(
		(playlist) => playlist.isSystemPlaylist || playlist.isDefaultFavorites,
	);
	if (systemPlaylist === undefined) return playlists;
	const others = playlists.filter(
		(playlist) =>
			playlist.id !== systemPlaylist.id && !playlist.isSystemPlaylist,
	);
	return [systemPlaylist, ...others];
}

function PlaylistTile({
	playlist,
	onTap,
}: Readonly<{ playlist: LibraryPlaylist; onTap: () => void }>) {
	const tileStyle: CSSProperties = {
		position: "relative",
		display: "block",
		width: "100%",
		aspectRatio: "1 / 1",
		border: 0,
		padding: 0,
		background: "var(--ds-surface)",
		cursor: "pointer",
		overflow: "hidden",
	};

	const overlayStyle: CSSProperties = {
		position: "absolute",
		left: 0,
		right: 0,
		bottom: 0,
		height: 40,
		background: "linear-gradient(to bottom, transparent, rgb(0 0 0 / 0.6))",
		pointerEvents: "none",
	};

	const countStyle: CSSProperties = {
		position: "absolute",
		left: 6,
		bottom: 6,
		display: "flex",
		alignItems: "center",
		gap: 4,
		fontSize: "0.72rem",
		color: "var(--ds-on-media-primary)",
		fontVariantNumeric: "tabular-nums",
	};

	return (
		<button
			type="button"
			style={tileStyle}
			onClick={onTap}
			aria-label={playlist.name}
		>
			{renderCover(playlist)}
			<div style={overlayStyle} aria-hidden="true" />
			<div style={countStyle} aria-hidden="true">
				<ArtistIcon name="music" size={12} strokeWidth={2} />
				<span>{playlist.trackCount}</span>
			</div>
		</button>
	);
}

function renderCover(playlist: LibraryPlaylist): ReactNode {
	const coverStyle: CSSProperties = {
		position: "absolute",
		inset: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover",
	};

	if (playlist.isDefaultFavorites || playlist.isSystemPlaylist) {
		const favoritesStyle: CSSProperties = {
			position: "absolute",
			inset: 0,
			display: "grid",
			placeItems: "center",
			background: FAVORITES_GRADIENT,
		};
		return (
			<span style={favoritesStyle} aria-hidden="true">
				<ArtistIcon
					name="sparkle"
					size={32}
					strokeWidth={2}
					style={{ color: "var(--ds-on-media-primary)" }}
				/>
			</span>
		);
	}

	if (playlist.coverImageURL !== null) {
		return (
			// biome-ignore lint/performance/noImgElement: collection cover image with dynamic inline styling; next/image not feasible on static export
			<img
				src={playlist.coverImageURL}
				alt=""
				style={coverStyle}
				loading="lazy"
			/>
		);
	}

	const placeholderStyle: CSSProperties = {
		position: "absolute",
		inset: 0,
		display: "grid",
		placeItems: "center",
	};
	return (
		<span style={placeholderStyle} aria-hidden="true">
			<PlaylistPlaceholder
				seed={playlist.id}
				size={56}
				className="library-collections-grid__placeholder"
			/>
		</span>
	);
}
